import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import {
  diagramToJson,
  createUserMessage,
  createSystemMessage,
  getWorkflowBackgroundSections,
  getEventsDataModelsBackgroundSections
} from '@/ai/utils/promptUtils'

export const buildSingleEntityPrompt = ({
  eventId,
  workflow,
  svgGraph,
  svgLanes,
  cardTypes,
  name,
  entityDescription
}) => {
  const messages = []

  messages.push(createSystemMessage(systemMessages.EVENT_MODELING_DDD_EXPERT))

  const promptSections = []

  const { workflow: currentDiagram } = workflow.eventsJson?.length
    ? diagramToJson(eventId, workflow, svgGraph, svgLanes)
    : { workflow: [], currentStep: undefined }
  const workflowBackgroundSections = getWorkflowBackgroundSections(workflow, currentDiagram, cardTypes)

  const currentEvent = svgGraph[eventId]

  promptSections.push(...workflowBackgroundSections)

  if (currentEvent) {
    const dataModelBackgroundSections = getEventsDataModelsBackgroundSections([currentEvent], currentDiagram, cardTypes)
    promptSections.push(...dataModelBackgroundSections)
  }

  promptSections.push({
    title: 'Request',
    content: `
      Review the information provided above, then generate an entity for the data field labeled - "${name}".
      The number of attributes/fields for the entity depends on the write and read models and can vary,
      but aim for 4–6 fields. Generate 3 rows of example data for the generated entity. One data field must be
      marked as the entity's primary key. Mark all mandatory fields in the entity with the "REQUIRED" tag.
      An entity name, along with the example data value, must each be at most 30 characters long.
      An entity name should be in human-readable format (e.g., 'Payment Method' instead of 'PaymentMethod').
    `
  })

  if (entityDescription?.trim()) {
    promptSections.push({
      title: 'Important Notes from User',
      content: [
        `The generated entity must align with the following description: "${entityDescription}"`,
        `This description should be treated as the most authoritative source, and in case of any conflicts with the
         workflow data, this description must take precedence. If there are any inconsistencies between the provided
         entity description and other metadata (e.g., workflow JSON, read models/command), always prioritize the
         provided entity description. The description might contain some unexpected or irrelevant details.
         Follow the instructions in the description only if they seem relevant and reasonable.`
      ]
    })
  }

  const entityExample = {
    entity: {
      name: 'Customer',
      dataFields: examples.entityExampleCustomer
    }
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('entity', JSON.stringify(entityExample))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
